/**
 * Generic Column Visibility Context
 * Provides shared state for column visibility across components
 */

import { createContext, useState, useEffect, useCallback, ReactNode, useContext } from 'react';

export interface ColumnConfig {
  id: string;
  label: string;
  isVisible: boolean;
  isLocked?: boolean; // For columns that cannot be hidden (e.g., invoice number)
}

interface ColumnVisibilityContextValue {
  columns: ColumnConfig[];
  updateColumns: (updatedColumns: ColumnConfig[]) => void;
  visibleColumnIds: string[];
  resetColumns: () => ColumnConfig[];
}

export const ColumnVisibilityContext = createContext<ColumnVisibilityContextValue | null>(null);

interface ColumnVisibilityProviderProps {
  children: ReactNode;
  defaultColumns: ColumnConfig[];
  storageKey: string;
}

export const ColumnVisibilityProvider = ({ children, defaultColumns, storageKey }: ColumnVisibilityProviderProps) => {
  // Helper to load from storage
  const loadColumnsFromStorage = useCallback((): ColumnConfig[] => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as ColumnConfig[];
        const defaultColumnsMap = new Map(defaultColumns.map((col) => [col.id, col]));

        // 1. Use stored columns to preserve order
        // Filter out any stored columns that no longer exist in defaultColumns
        const mergedColumns = parsed
          .filter((col) => defaultColumnsMap.has(col.id))
          .map((col) => {
            const defaultCol = defaultColumnsMap.get(col.id)!;
            return {
              ...col,
              label: defaultCol.label, // Update label from code
              isLocked: defaultCol.isLocked, // Always use locked status from code
            };
          });

        // 2. Add any new columns from defaultColumns that are missing in storage
        const storedIds = new Set(parsed.map((col) => col.id));
        const newColumns = defaultColumns.filter((col) => !storedIds.has(col.id));

        return [...mergedColumns, ...newColumns];
      }
    } catch (error) {
      console.error(`Failed to load column visibility from localStorage (${storageKey}):`, error);
    }
    return defaultColumns;
  }, [defaultColumns, storageKey]);

  const [columns, setColumns] = useState<ColumnConfig[]>(() => loadColumnsFromStorage());

  // Save to localStorage whenever columns change
  useEffect(() => {
    const currentColumns = loadColumnsFromStorage();
    setColumns(currentColumns);
  }, [storageKey]);

  // Save to localStorage whenever columns change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(columns));
    } catch (error) {
      console.error(`Failed to save column visibility to localStorage (${storageKey}):`, error);
    }
  }, [columns, storageKey]);

  const updateColumns = useCallback((updatedColumns: ColumnConfig[]) => {
    setColumns(updatedColumns);
  }, []);

  const resetColumns = useCallback(() => defaultColumns, [defaultColumns]);

  const visibleColumnIds = columns.filter((col) => col.isVisible).map((col) => col.id);

  const value: ColumnVisibilityContextValue = {
    columns,
    updateColumns,
    visibleColumnIds,
    resetColumns,
  };

  return <ColumnVisibilityContext.Provider value={value}>{children}</ColumnVisibilityContext.Provider>;
};

// Hook to consume the context
export const useColumnVisibility = () => {
  const context = useContext(ColumnVisibilityContext);

  if (!context) {
    throw new Error('useColumnVisibility must be used within ColumnVisibilityProvider');
  }

  return context;
};
